import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { groupHash } from '../../utils/groupHash';
import { errorText } from '../../utils/errorText';
import { box, button, input, label } from '../shared/styles';

/*
 * A protocol, editable at any time (Task 6.4, decided 2026-09-26).
 *
 * Nothing here is ever locked for the person who may edit it: steps,
 * parameters, materials and equipment are added, changed, reordered and
 * removed freely. What a save requires is a reason and a signature -- the
 * server records both on a new version, and the history below shows every
 * one. Someone else's variant is read here, not edited: the way to change it
 * is to make your own.
 */

const BLANK = {
  title: '', objective: '',
  equipment: [{ type: '', model: '' }],
  materials: [],
  steps: [{ text: '', duration_min: null, parameters: {} }],
};

const numberish = (v) => (/^-?\d+(\.\d+)?$/.test(String(v).trim()) ? Number(v) : v);

function fromVersion(v) {
  if (!v) return BLANK;
  return {
    title: v.title || '', objective: v.objective || '',
    equipment: (v.equipment || []).map((e) => ({ type: e.type || '', model: e.model || '' })),
    materials: (v.materials || []).map((m) => ({ name: m.name || '', amount: m.amount || '', unit: m.unit || '' })),
    steps: (v.steps || []).map((s) => ({ ...s, parameters: { ...(s.parameters || {}) } })),
  };
}

export function toContent(c) {
  const out = {
    title: c.title.trim(),
    objective: c.objective.trim() || null,
    equipment: c.equipment.filter((e) => e.type.trim())
      .map((e) => ({ type: e.type.trim(), model: e.model.trim() || null })),
    materials: c.materials.filter((m) => m.name.trim())
      .map((m) => ({ name: m.name.trim(), amount: String(m.amount).trim() || null, unit: m.unit.trim() || null })),
    steps: c.steps.filter((s) => s.text.trim()).map((s) => {
      const step = {
        text: s.text.trim(),
        duration_min: s.duration_min === '' || s.duration_min == null ? null : Number(s.duration_min),
        parameters: s.parameters || {},
      };
      if (s.cites) step.cites = s.cites;
      return step;
    }),
  };
  return out;
}

export default function ProtocolEditor({ detail, canEdit, notYours, canAuthor, isNew, onSave, onVariant }) {
  const { t } = useTranslation('protocols');
  const [content, setContent] = useState(() => fromVersion(detail?.version));
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [attest, setAttest] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [variant, setVariant] = useState(null); // {reason, attest} while the panel is open

  const set = (field) => (e) => setContent((c) => ({ ...c, [field]: e.target.value }));
  const setRow = (list, i, field) => (e) => {
    const value = e.target.value;
    setContent((c) => ({ ...c, [list]: c[list].map((row, j) => (j === i ? { ...row, [field]: value } : row)) }));
  };
  const addRow = (list, row) => () => setContent((c) => ({ ...c, [list]: [...c[list], row] }));
  const removeRow = (list, i) => () => setContent((c) => ({ ...c, [list]: c[list].filter((_, j) => j !== i) }));
  const move = (i, delta) => () => setContent((c) => {
    const steps = [...c.steps];
    const j = i + delta;
    if (j < 0 || j >= steps.length) return c;
    [steps[i], steps[j]] = [steps[j], steps[i]];
    return { ...c, steps };
  });
  const setParam = (i, oldKey, field) => (e) => {
    const value = e.target.value;
    setContent((c) => ({
      ...c,
      steps: c.steps.map((s, j) => {
        if (j !== i) return s;
        const entries = Object.entries(s.parameters || {}).map(([k, v]) => (k === oldKey
          ? (field === 'key' ? [value, v] : [k, numberish(value)]) : [k, v]));
        return { ...s, parameters: Object.fromEntries(entries.filter(([k]) => k !== '' || field === 'key')) };
      }),
    }));
  };
  const addParam = (i) => () => setContent((c) => ({
    ...c, steps: c.steps.map((s, j) => (j === i ? { ...s, parameters: { ...s.parameters, [`parameter_${Object.keys(s.parameters || {}).length + 1}`]: '' } } : s)),
  }));

  const body = toContent(content);
  const ready = canEdit && reason.trim().length >= 3 && attest && body.title.length >= 3 && body.steps.length > 0
    && (!isNew || name.trim());

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await onSave({ ...(isNew ? { name: name.trim() } : {}), ...body, reason: reason.trim(), attest: true });
      setReason('');
      setAttest(false);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const ro = !canEdit;
  return (
    <div style={box}>
      {isNew && (
        <label style={label}>{t('name')}
          <input data-testid="protocol-name" title={t('tip.name')} value={name} onChange={(e) => setName(e.target.value)} style={input} />
        </label>
      )}
      {notYours && <p data-testid="not-yours" style={{ color: 'var(--c-warning-fg)' }}>{t('notYours')}</p>}
      <label style={label}>{t('title')}
        <input data-testid="title" title={t('tip.title')} value={content.title} disabled={ro} onChange={set('title')} style={input} />
      </label>
      <label style={label}>{t('objective')}
        <textarea data-testid="objective" title={t('tip.objective')} rows={2} value={content.objective} disabled={ro} onChange={set('objective')} style={input} />
      </label>

      <h4>{t('equipment')}</h4>
      {content.equipment.map((e, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
          <input data-testid={`equipment-type-${i}`} title={t('tip.equipmentType')} placeholder={t('equipmentType')} value={e.type} disabled={ro}
            onChange={setRow('equipment', i, 'type')} style={input} />
          <input data-testid={`equipment-model-${i}`} title={t('tip.equipmentModel')} placeholder={t('equipmentModel')} value={e.model} disabled={ro}
            onChange={setRow('equipment', i, 'model')} style={input} />
          {!ro && <button type="button" onClick={removeRow('equipment', i)} title={t('tip.removeEquipment')} aria-label={t('remove')}>✕</button>}
        </div>
      ))}
      {!ro && <button type="button" data-testid="add-equipment" title={t('tip.addEquipment')} onClick={addRow('equipment', { type: '', model: '' })}>{t('addEquipment')}</button>}

      <h4>{t('materials')}</h4>
      {content.materials.map((m, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
          <input data-testid={`material-name-${i}`} title={t('tip.materialName')} placeholder={t('materialName')} value={m.name} disabled={ro} onChange={setRow('materials', i, 'name')} style={input} />
          <input data-testid={`material-amount-${i}`} title={t('tip.amount')} placeholder={t('amount')} value={m.amount} disabled={ro} onChange={setRow('materials', i, 'amount')} style={input} />
          <input data-testid={`material-unit-${i}`} title={t('tip.unit')} placeholder={t('unit')} value={m.unit} disabled={ro} onChange={setRow('materials', i, 'unit')} style={input} />
          {!ro && <button type="button" onClick={removeRow('materials', i)} title={t('tip.removeMaterial')} aria-label={t('remove')}>✕</button>}
        </div>
      ))}
      {!ro && <button type="button" data-testid="add-material" title={t('tip.addMaterial')} onClick={addRow('materials', { name: '', amount: '', unit: '' })}>{t('addMaterial')}</button>}

      <h4>{t('steps')}</h4>
      <ol style={{ paddingInlineStart: 20 }}>
        {content.steps.map((s, i) => (
          <li key={i} style={{ marginBottom: 10 }}>
            <textarea data-testid={`step-text-${i}`} title={t('tip.stepText')} rows={2} value={s.text} disabled={ro}
              onChange={setRow('steps', i, 'text')} style={{ ...input, width: '100%' }} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <label>{t('duration')}{' '}
                <input data-testid={`step-duration-${i}`} title={t('tip.stepDuration')} type="number" min={0} value={s.duration_min ?? ''} disabled={ro}
                  onChange={setRow('steps', i, 'duration_min')} style={{ ...input, width: 100 }} />
              </label>
              {Object.entries(s.parameters || {}).map(([k, v]) => (
                <span key={k} style={{ display: 'inline-flex', gap: 4 }}>
                  <input aria-label={t('parameter')} title={t('tip.parameterKey')} value={k} disabled={ro} onChange={setParam(i, k, 'key')} style={{ ...input, width: 130 }} />
                  <input aria-label={t('value')} title={t('tip.parameterValue')} value={v ?? ''} disabled={ro} onChange={setParam(i, k, 'value')} style={{ ...input, width: 80 }} />
                </span>
              ))}
              {!ro && (
                <>
                  <button type="button" onClick={addParam(i)} title={t('tip.addParameter')}>{t('addParameter')}</button>
                  <button type="button" data-testid={`step-up-${i}`} onClick={move(i, -1)} title={t('tip.moveUp')} aria-label={t('moveUp')}>↑</button>
                  <button type="button" data-testid={`step-down-${i}`} onClick={move(i, 1)} title={t('tip.moveDown')} aria-label={t('moveDown')}>↓</button>
                  <button type="button" data-testid={`remove-step-${i}`} onClick={removeRow('steps', i)} title={t('tip.removeStep')} aria-label={t('remove')}>✕</button>
                </>
              )}
              {s.cites?.length > 0 && <small>{t('cites')} {s.cites.join(', ')}</small>}
            </div>
          </li>
        ))}
      </ol>
      {!ro && <button type="button" data-testid="add-step" title={t('tip.addStep')} onClick={addRow('steps', { text: '', duration_min: null, parameters: {} })}>{t('addStep')}</button>}

      {canEdit && (
        <div style={{ ...box, marginTop: 12 }}>
          <label style={label}>{t('reason')}
            <textarea data-testid="change-reason" title={t('tip.reason')} rows={2} value={reason} maxLength={2000}
              onChange={(e) => setReason(e.target.value)} style={input} />
          </label>
          <label style={{ ...label, flexDirection: 'row', gap: 8 }}>
            <input type="checkbox" data-testid="attest" title={t('tip.attest')} checked={attest} onChange={() => setAttest((v) => !v)} />
            {t('attest')}
          </label>
          {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
          <button type="button" data-testid="save" title={isNew ? t('tip.create') : t('tip.saveVersion')} disabled={busy || !ready} onClick={save} style={button('var(--c-primary)')}>
            {isNew ? t('create') : t('saveVersion')}
          </button>
        </div>
      )}

      {!isNew && canAuthor && (
        variant
          ? (
            <div style={{ ...box, marginTop: 12 }}>
              <label style={label}>{t('variantReason')}
                <input data-testid="variant-reason" title={t('tip.variantReason')} value={variant.reason}
                  onChange={(e) => setVariant((v) => ({ ...v, reason: e.target.value }))} style={input} />
              </label>
              <label style={{ ...label, flexDirection: 'row', gap: 8 }}>
                <input type="checkbox" data-testid="variant-attest" title={t('tip.attest')} checked={variant.attest}
                  onChange={() => setVariant((v) => ({ ...v, attest: !v.attest }))} />
                {t('attest')}
              </label>
              <button type="button" data-testid="variant-create" title={t('tip.createVariant')}
                disabled={variant.reason.trim().length < 3 || !variant.attest}
                onClick={async () => {
                  try {
                    await onVariant({ reason: variant.reason.trim(), attest: true });
                    setVariant(null);
                  } catch (e) {
                    setError(errorText(e));
                  }
                }} style={button('var(--c-primary)')}>
                {t('createVariant')}
              </button>
            </div>
          )
          : <button type="button" data-testid="make-variant" title={t('tip.makeVariant')} onClick={() => setVariant({ reason: '', attest: false })}
              style={{ marginTop: 12 }}>{t('makeVariant')}</button>
      )}

      {detail?.history?.length > 0 && (
        <div data-testid="history" style={{ marginTop: 16 }}>
          <h4>{t('history')}</h4>
          <ol reversed style={{ paddingInlineStart: 20 }}>
            {detail.history.map((h) => (
              <li key={h.version} data-testid={`history-${h.version}`} style={{ marginBottom: 6 }}>
                v{h.version} · {h.signed_by_username || h.signed_by} · {new Date(h.signed_at).toLocaleString()}
                {' — '}{h.change_reason}
                {h.source?.mode && h.source.mode !== 'manual' ? ` (${t(`source.${h.source.mode}`)})` : ''}
                <br />
                <small style={{ fontFamily: 'ui-monospace, monospace' }}>{t('signature')} {groupHash(h.signature, 4)}</small>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
